import type { Component } from "vue";
import { DxPopup } from "devextreme-vue/popup";
import type { messageBoxButtons, messageBoxIcon } from "../components/dialogs/message-box";
import { messageBoxResult } from "../components/dialogs/message-box";
import MessageBox from "../components/dialogs/MessageBox.vue";
import InputBox from "../components/dialogs/InputBox.vue";
import InputNumberBox from "../components/dialogs/InputNumberBox.vue";
import UploadDocumentBox from "../components/dialogs/UploadDocumentBox.vue";
import ViewerDocumentBox from "../components/dialogs/ViewerDocumentBox.vue";
import type { IUploadDocumentBoxProps } from "../components/dialogs/models/IUploadDocumentBoxProps";
import type { IViewerDocumentBoxProps } from "../components/dialogs/models/IViewerDocumentBoxProps";

type OnModalShownCallback = (request: {
  component: Component;
  props?: Record<string, unknown>;
  popupProps?: Omit<DxPopup, "toolbarItems">;
  promiseResolver: (value: unknown) => void;
}) => void;

let instance: ReturnType<typeof useModalServiceInternal> | null;
const defaultPopupOptions: Omit<DxPopup, "toolbarItems" | "showCloseButton"> = { dragEnabled: false, hideOnOutsideClick: false };

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useModalServiceInternal() {
  let onModalShownCallback: OnModalShownCallback | undefined;

  function showModal(component: Component, props?: Record<string, unknown>, popupProps?: Omit<DxPopup, "toolbarItems" | "showCloseButton">): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (onModalShownCallback) onModalShownCallback({ component, props, popupProps: { ...popupProps, ...defaultPopupOptions }, promiseResolver: resolve });
      else reject();
    });
  }

  function messageBox(content: string, title: string, buttons: messageBoxButtons, icon: messageBoxIcon, isHtml?: boolean, width?: number, height?: number): Promise<unknown> {
    return showModal(
      MessageBox,
      { content, buttons, icon, isHtml },
      {
        width: width ?? "500",
        height: height ?? "auto",
        title: title,
        showTitle: true,
      },
    );
  }

  async function inputBox(label: string, dxInputType: "dxTextBox" | "dxTextArea", dxInputProps?: any, required?: boolean): Promise<string | null | undefined> {
    const popUpProps: Omit<DxPopup, "toolbarItems" | "showCloseButton"> = {
      width: "500",
      height: dxInputType === "dxTextBox" ? "200" : "auto",
      showTitle: true,
    };

    const inputResponse = (await showModal(InputBox, { label, dxInputType, dxInputProps, required }, popUpProps)) as { result: string; value: string | null };
    if (inputResponse.result === messageBoxResult.ok) {
      return inputResponse.value;
    }
    return undefined;
  }

  async function inputNumberBox(label: string,dxInputProps?: any,required?: boolean): Promise<number | null | undefined> {
    const popUpProps: Omit<DxPopup, "toolbarItems" | "showCloseButton"> = {
      width: "500",
      height: "220",
      showTitle: true,
    };

    const inputResponse = (await showModal(InputNumberBox, { label, dxInputProps, required }, popUpProps)) as {
      result: string;
      value: number | null;
    };

    if (inputResponse.result === messageBoxResult.ok) {
      return inputResponse.value;
    }
    
    return undefined;
  }

  async function uploadFileBox(boxProps: IUploadDocumentBoxProps, title?: string): Promise<File | undefined> {
    const popUpProps: Omit<DxPopup, "toolbarItems" | "showCloseButton"> = {
      width: "500",
      height: "auto",
      showTitle: !!title,
      title: title,
    };

    return showModal(UploadDocumentBox, { ...boxProps }, popUpProps).then((result: any) => {
      if (result.result === messageBoxResult.ok) {
        return result.value;
      }
      return undefined;
    });
  }

  async function viewerDocumentBox(boxProps: IViewerDocumentBoxProps, title?: string): Promise<unknown> {
    const popUpProps: Omit<DxPopup, "toolbarItems" | "showCloseButton"> = {
      width: "500",
      height: "auto",
      showTitle: !!title,
      title: title,
    };

    return showModal(ViewerDocumentBox, { ...boxProps }, popUpProps);
  }

  function onModalShowRequest(callback: OnModalShownCallback): void {
    onModalShownCallback = callback;
  }

  // noinspection JSUnusedGlobalSymbols
  return {
    showModal,
    messageBox,
    inputBox,
    inputNumberBox,
    uploadFileBox,
    viewerDocumentBox,
    onModalShowRequest: onModalShowRequest,
  };
}

export function useModalService(): ReturnType<typeof useModalServiceInternal> {
  if (instance == null) instance = useModalServiceInternal();
  return instance;
}
